import type React from 'react';

import { Box, FormField, Heading, Input, Label, Paragraph } from '@zillow/constellation-10';

import type { Section } from '../../../models/index';
import { PROPERTY_ADDRESS } from '../../../constants/index';
import { isMultiFamily } from './Utilities/MultiFamilyLandlordUtility';
import { FULLSTORY_EXCLUDE } from '../../../constants/analytics';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function PropertyAddress_v3({ section, setSectionValues }: Props) {
  function onStreetChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.STREET]: value,
    });
  }

  function onAddressLine2Change(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.ADDRESS_LINE_2]: value,
    });
  }

  function onCityChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.CITY]: value,
    });
  }

  function onStateChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.STATE]: value,
    });
  }

  function onZipChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.ZIP]: value,
    });
  }

  function onMFStreetChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_STREET]: value,
    });
  }

  function onMFAddressLine2Change(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_ADDRESS_LINE_2]: value,
    });
  }

  function onMFCityChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_CITY]: value,
    });
  }

  function onMFStateChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_STATE]: value,
    });
  }

  function onMFZipChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_ZIP]: value,
    });
  }

  const isMultiFamilyLandlord = isMultiFamily(section);

  return (
    <>
      <Heading level={3} textStyle="responsive.heading-xs">
        What is the property’s address?
      </Heading>
      <FormField
        required
        className={FULLSTORY_EXCLUDE}
        css={{ marginTop: 'layout.tighter' }}
        label={<Label>Street address</Label>}
        control={
          <Input
            aria-labelledby="property-address-street"
            value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.STREET)}
            onChange={onStreetChange}
            error={section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS)}
          />
        }
      />
      <FormField
        className={FULLSTORY_EXCLUDE}
        css={{ marginTop: 'layout.tighter' }}
        label={<Label optional>Address line 2</Label>}
        control={
          <Input
            aria-labelledby="property-address-line-2"
            value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.ADDRESS_LINE_2)}
            onChange={onAddressLine2Change}
          />
        }
      />
      <FormField
        required
        className={FULLSTORY_EXCLUDE}
        css={{ marginTop: 'layout.tighter' }}
        label={<Label>City</Label>}
        control={
          <Input
            aria-labelledby="property-address-city"
            value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.CITY)}
            onChange={onCityChange}
          />
        }
      />
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gap: 'layout.tighter',
          marginTop: 'layout.tighter',
        }}
      >
        <Box css={{ gridColumnEnd: { base: 'span 12', sm: 'span 6' } }}>
          <FormField
            required
            className={FULLSTORY_EXCLUDE}
            label={<Label>State</Label>}
            control={
              <Input
                aria-labelledby="property-address-state"
                value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.STATE)}
                onChange={onStateChange}
              />
            }
          />
        </Box>
        <Box css={{ gridColumnEnd: { base: 'span 12', sm: 'span 6' } }}>
          <FormField
            required
            className={FULLSTORY_EXCLUDE}
            label={<Label>ZIP code</Label>}
            control={
              <Input
                aria-labelledby="property-address-zip"
                value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.ZIP)}
                type="number"
                onChange={onZipChange}
              />
            }
          />
        </Box>
      </Box>
      {isMultiFamilyLandlord && (
        <>
          <Paragraph css={{ marginTop: 'md' }} textStyle="body-lg-bold">
            What is the multifamily property address?
          </Paragraph>
          <FormField
            required
            className={FULLSTORY_EXCLUDE}
            css={{ marginTop: 'layout.tighter' }}
            label={<Label>Street address</Label>}
            control={
              <Input
                aria-labelledby="property-address-multifamily-street"
                value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_STREET)}
                onChange={onMFStreetChange}
                error={section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS)}
              />
            }
          />
          <FormField
            className={FULLSTORY_EXCLUDE}
            css={{ marginTop: 'layout.tighter' }}
            label={<Label optional>Address line 2</Label>}
            control={
              <Input
                aria-labelledby="property-address-multifamily-line-2"
                value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_ADDRESS_LINE_2)}
                onChange={onMFAddressLine2Change}
              />
            }
          />
          <FormField
            required
            className={FULLSTORY_EXCLUDE}
            css={{ marginTop: 'layout.tighter' }}
            label={<Label>City</Label>}
            control={
              <Input
                aria-labelledby="property-address-multifamily-city"
                value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_CITY)}
                onChange={onMFCityChange}
              />
            }
          />
          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: 'repeat(12, 1fr)',
              gap: 'layout.tighter',
              marginTop: 'layout.tighter',
            }}
          >
            <Box css={{ gridColumnEnd: { base: 'span 12', sm: 'span 6' } }}>
              <FormField
                required
                className={FULLSTORY_EXCLUDE}
                label={<Label>State</Label>}
                control={
                  <Input
                    aria-labelledby="property-address-multifamily-state"
                    value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_STATE)}
                    onChange={onMFStateChange}
                  />
                }
              />
            </Box>
            <Box css={{ gridColumnEnd: { base: 'span 12', sm: 'span 6' } }}>
              <FormField
                required
                className={FULLSTORY_EXCLUDE}
                label={<Label>ZIP code</Label>}
                control={
                  <Input
                    aria-labelledby="property-address-multifamily-zip"
                    value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_ZIP)}
                    type="text"
                    onChange={onMFZipChange}
                  />
                }
              />
            </Box>
          </Box>
        </>
      )}
    </>
  );
}

PropertyAddress_v3.description = 'Split out street address, city, state, zip';

export default PropertyAddress_v3;
